import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/70 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 transition-shadow cursor-pointer",
  {
    variants: {
      variant: {
        default: "button-primary bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "button-destructive bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        outline:
          "button-outline border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        outline2:
          "button-outline2 outline outline-1 outline-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "button-secondary bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "button-ghost hover:bg-accent hover:text-accent-foreground",
        link: "button-link text-primary underline-offset-4 hover:underline",
        menu: "button-menu text-muted-foreground transition-all hover:text-primary justify-start w-full"
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-7 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "button-icon h-9 w-9 justify-center shrink-0"
      },
      effect: {
        shine:
          "before:animate-shine relative overflow-hidden before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.5)_50%,transparent_75%,transparent_100%)] before:bg-[length:250%_250%,100%_100%] before:bg-no-repeat background-position_0s_ease"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  effect?: "shine";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, effect, asChild = false, loading = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    if (loading)
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, effect, className }))}
          ref={ref}
          {...props}
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <AnimatePresence>
            {loading ? (
              <motion.div
                key="loader"
                className="absolute inset-0 z-10 flex items-center justify-center"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={{
                  hidden: { opacity: 0, y: 10 },
                  visible: { opacity: 1, y: 0 }
                }}
              >
                <span className="sr-only">Laddar...</span>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                >
                  <LoaderCircle className="h-4 w-4" />
                </motion.div>
              </motion.div>
            ) : null}
            <span className="invisible">{props.children}</span>
          </AnimatePresence>
        </Comp>
      );
    return (
      <Comp
        className={`${loading ? "invisible" : "visible"} ${cn(buttonVariants({ variant, size, effect, className }))}`}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
